import React from 'react';
import { Box, Typography } from '@mui/material';

import { Component, PlateTypeProperties } from '@paragon/contract-library';
import { PhysicalComponentVisual, ThreeJsScene } from '@paragon/three-js-truss-scene';

import { useAppSelector } from '../hooks';

const componentPreviewWidth = 400;
const componentPreviewHeight = 200;

const formatNames = <T extends { readonly name: string }>(arrayOfNamedObjects: readonly T[]) =>
  arrayOfNamedObjects.map((object) => {
    const nameSplitBySpace = object.name.split(' ');
    return { ...object, name: nameSplitBySpace[nameSplitBySpace.length - 1] };
  });

const renderThreeJsScene = (
  component: Component,
  fileType: string,
  plateTypeProperties: { [getPlateTypeStringResult: string]: PlateTypeProperties | undefined },
) => (
  <Box sx={{ flex: 1, m: 5 }}>
    <ThreeJsScene width={componentPreviewWidth} height={componentPreviewHeight}>
      <PhysicalComponentVisual
        members={formatNames(component.members)}
        platePairs={formatNames(component.platePairs)}
        plateTypeProperties={plateTypeProperties}
        component={component}
        showComponentLabel={false}
        showPieceLabels
      />
    </ThreeJsScene>
    <Typography sx={{ textAlign: 'center' }}>{fileType}</Typography>
  </Box>
);

export function ComparisonResults() {
  const comparisonResults = useAppSelector((state) => state.comparisonResults);

  let comparisonResultBlocks;
  let failedFilesBlock;
  if (comparisonResults != null) {
    if (Object.keys(comparisonResults.comparisons).length === 0) {
      comparisonResultBlocks = <span className="no-matches">No matches found.</span>;
    } else {
      comparisonResultBlocks = Object.entries(comparisonResults.comparisons).map(([name, results]) => {
        const comparisonEquivalent = results.diff === '';
        const bgcolor = comparisonEquivalent ? 'green' : 'red';
        return (
          <Box key={name} sx={{ display: 'flex' }}>
            {renderThreeJsScene(results.set1Component, results.set1FileType, results.plateTypeProperties)}
            <Box sx={{ flex: 1, m: 5 }}>
              <Box sx={{ bgcolor, p: 1 }}>{name}</Box>
              <Box>
                {results.diff.split('\r\n').map((line) => (
                  <Box key={line} sx={{ p: 1 }}>
                    {line}
                    <br />
                  </Box>
                ))}
              </Box>
            </Box>
            {renderThreeJsScene(results.set2Component, results.set2FileType, results.plateTypeProperties)}
          </Box>
        );
      });
    }

    const failedFiles = comparisonResults.failedFiles.map((failedFile) => (
      <Box key={failedFile.guid} sx={{ p: 1 }}>
        <div>{`${failedFile.name}.${failedFile.fileType}`}</div>
        <div title={(failedFile.exception as any).StackTraceString}>{(failedFile.exception as any).Message}</div>
      </Box>
    ));
    if (failedFiles.length > 0) {
      failedFilesBlock = (
        <Box sx={{ width: '50%', border: 'solid 1px #333', mx: 'auto' }}>
          <Typography variant="h6" sx={{ textAlign: 'center', p: 1 }}>
            Failed Files:
          </Typography>
          <br />
          {failedFiles}
        </Box>
      );
    }
  }

  return (
    <>
      {failedFilesBlock}
      {comparisonResultBlocks}
    </>
  );
}
